import type { Settings } from '@bioforge/protocol';
import type { ApiClient } from '../api/api-client.js';
import { button, el, overlayRoot, panel } from './html.js';

/**
 * Настройки игрока (SPEC.md 17.3, 20.4): звук, анимации, подсказки. Хранятся в профиле на сервере.
 * Переназначение клавиш — с экраном настроек по макетам (этап 7); сохранённые клавиши не теряются.
 */
export class SettingsOverlay {
  private readonly root = overlayRoot();
  private readonly onKey = (event: KeyboardEvent): void => {
    event.stopPropagation();
    if (event.key === 'Escape') this.close(null);
  };

  constructor(
    api: ApiClient,
    current: Settings,
    private readonly onDone: (saved: Settings | null) => void,
  ) {
    const form = panel('440px');
    const volume = (caption: string, value: number) => {
      const label = el('label', { display: 'flex', flexDirection: 'column', gap: '4px' }, caption);
      const input = el('input');
      input.type = 'range';
      input.min = '0';
      input.max = '100';
      input.value = String(Math.round(value * 100));
      label.append(input);
      return { label, read: () => Number(input.value) / 100 };
    };
    const check = (caption: string, value: boolean) => {
      const label = el('label', { display: 'flex', gap: '8px', alignItems: 'center' });
      const input = el('input', { width: '20px', height: '20px' });
      input.type = 'checkbox';
      input.checked = value;
      label.append(input, caption);
      return { label, read: () => input.checked };
    };
    const music = volume('Музыка', current.sound.music);
    const effects = volume('Звуки', current.sound.effects);
    const voices = volume('Голоса существ', current.sound.voices);
    const musicMuted = check('Выключить музыку', current.sound.musicMuted);
    const effectsMuted = check('Выключить звуки', current.sound.effectsMuted);
    const hints = check('Карточки знакомства и подсказки', current.accessibility.hints);

    const motionLabel = el(
      'label',
      { display: 'flex', flexDirection: 'column', gap: '4px' },
      'Анимации',
    );
    const motion = el('select', {
      height: '40px',
      borderRadius: '10px',
      font: '16px system-ui, sans-serif',
    });
    for (const [value, text] of [
      ['system', 'Как в системе'],
      ['off', 'Все анимации'],
      ['on', 'Меньше движения'],
    ] as const) {
      const option = el('option', {}, text);
      option.value = value;
      option.selected = current.accessibility.reducedMotion === value;
      motion.append(option);
    }
    motionLabel.append(motion);

    const error = el('p', { margin: '0', color: '#ffb3c8', minHeight: '1.2em' });
    error.setAttribute('role', 'alert');
    const actions = el('div', { display: 'flex', gap: '12px', justifyContent: 'flex-end' });
    const cancel = button('Отмена', '#24506b');
    cancel.addEventListener('click', () => this.close(null));
    const save = button('Сохранить', '#1b7d74');
    save.addEventListener('click', () => {
      const settings: Settings = {
        keys: current.keys,
        sound: {
          music: music.read(),
          effects: effects.read(),
          voices: voices.read(),
          musicMuted: musicMuted.read(),
          effectsMuted: effectsMuted.read(),
        },
        accessibility: {
          reducedMotion: motion.value as Settings['accessibility']['reducedMotion'],
          hints: hints.read(),
        },
      };
      save.disabled = true;
      error.textContent = '';
      api.saveSettings(settings).then(
        (saved) => this.close(saved),
        () => {
          error.textContent = 'Не удалось сохранить. Попробуйте ещё раз.';
          save.disabled = false;
        },
      );
    });
    actions.append(cancel, save);

    form.append(
      el('h2', { margin: '0' }, 'Настройки'),
      music.label,
      effects.label,
      voices.label,
      musicMuted.label,
      effectsMuted.label,
      motionLabel,
      hints.label,
      el(
        'p',
        { margin: '0', color: '#9fc4d6', fontSize: '14px' },
        'Настройки хранятся в аккаунте и действуют на всех устройствах. Звук появится позже.',
      ),
      error,
      actions,
    );
    this.root.append(form);
    document.body.appendChild(this.root);
    window.addEventListener('keydown', this.onKey, true);
    save.focus();
  }

  private close(saved: Settings | null): void {
    this.destroy();
    this.onDone(saved);
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKey, true);
    this.root.remove();
  }
}
